import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import {
  EnvironmentOutlined,
  ShopOutlined,
  TagsOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { CentralizedAuditLogRecordDTO } from '@recurrency/core-api-schema/dist/auditLog/getCentralizedAuditLog';
import { SortDirection, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { ColumnType } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import { useDebounce } from 'use-debounce/lib';
import { ZipCelXSheet } from 'zipcelx';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { MultiSelectOption, UseAsyncMultiSelectProps } from 'components/AsyncSelect/types';
import {
  fuzzyFilterAndHighlight,
  UseAsyncSelectProps,
  useLocationsSelectProps,
  useSuppliersSelectProps,
  useAuditLogUsersSelectProps,
} from 'components/AsyncSelect/useAsyncSelectProps';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { DateRangePicker } from 'components/DatePicker';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { PlanningLink } from 'components/Links';
import { PageHeader } from 'components/PageHeader';
import { DownloadButton, DownloadXSheetColumn, recordsToXSheet } from 'components/recipes/DownloadButton';
import { ResultCount } from 'components/ResultCount';
import { DEFAULT_PAGE_SIZE } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { coreApiFetch, fetchAllRecordsInBatches } from 'utils/api';
import { truthy } from 'utils/boolean';
import {
  capitalizeFirst,
  formatDate,
  formatName,
  joinIdNameObj,
  snakeCaseToTitleCase,
  splitIdNameStr,
} from 'utils/formatting';
import { objMapValues, objRemoveUndefinedValues } from 'utils/object';
import { routes, useHashState } from 'utils/routes';
import { asKeyOf, sortableDateColumn, sortableStringColumn } from 'utils/tables';

import { CentralizedAuditLogsPageHashState } from 'types/hash-state';

const FIELD_VALUES = [
  'excluded',
  'excluded_until',
  'flagged',
  'lead_time',
  'min_order_qty',
  'min_stock',
  'max_stock',
  'stock_floor',
  'plannable',
  'replenishment_method',
  'safety_stock_days',
  'stockable',
  'forecast_override',
  'usage_override',
  'order_cycle_days',
  'order_cycle_source',
  'review_cycle',
  'order_cycle_days_override',
  'review_cycle_days',
  'max_shelf_life_days',
  'max_shelf_life_reason',
] as const;

const FIELD_OPTIONS: MultiSelectOption[] = FIELD_VALUES.map((value) => ({
  value,
  label: value.split('_').map(capitalizeFirst).join(' '),
}));

/** Maps `useCoreApiTableProps` sort state to API `SortDirection` enum (values are both lowercase `asc` / `desc`). */
function coreApiTableSortDirToSortDirection(sortDir: 'asc' | 'desc' | undefined): SortDirection | undefined {
  if (sortDir === 'asc') return SortDirection.Asc;
  if (sortDir === 'desc') return SortDirection.Desc;
  return undefined;
}

function useAuditLogItemSelectProps(): UseAsyncSelectProps {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const { data, isLoading } = useCoreApi(schemas.items.getItemsV2, {
    queryParams: { searchQuery: debouncedSearchQuery, limit: 100 },
  });

  const options = useMemo(
    () =>
      (data?.items || []).map((item) => ({
        value: item.itemId,
        label: `${item.itemCode}: ${item.itemName}`,
      })),
    [data?.items],
  );

  return {
    isLoading,
    options: fuzzyFilterAndHighlight(options as Any, searchQuery, ['label', 'value']),
    searchQuery,
    setSearchQuery,
  };
}

export const CentralizedAuditLogsPage = () => {
  const [hashState, updateHashState] = useHashState<CentralizedAuditLogsPageHashState>();
  const tenantsUsersSelectProps = useAuditLogUsersSelectProps();
  const itemSelectProps = useAuditLogItemSelectProps();
  const locationsSelectProps = useLocationsSelectProps({});
  const suppliersSelectProps = useSuppliersSelectProps();

  const pageNum = hashState.page || 1;

  /** One pair of local calendar dates for the session when URL has no `dateRange` — avoids `moment()` drift on recompute/render. */
  const sessionDefaultFrom = useMemo(() => moment().subtract(1, 'month').format('YYYY-MM-DD'), []);
  const sessionDefaultTo = useMemo(() => moment().format('YYYY-MM-DD'), []);

  /** Shared filter for table + download; local calendar dates match DateRangePicker. */
  const filterForApi = useMemo(
    () =>
      objRemoveUndefinedValues({
        updatedByUserId: hashState.userId,
        itemUid: hashState.itemUid,
        locationId: hashState.locationValue ? splitIdNameStr(hashState.locationValue).foreignId : undefined,
        supplierId: hashState.supplierValue ? splitIdNameStr(hashState.supplierValue).foreignId : undefined,
        fields: hashState.fields?.length ? hashState.fields : undefined,
        updatedAtFrom: hashState.dateRange?.[0] ?? sessionDefaultFrom,
        updatedAtTo: hashState.dateRange?.[1] ?? sessionDefaultTo,
      }),
    [
      hashState.userId,
      hashState.itemUid,
      hashState.locationValue,
      hashState.supplierValue,
      hashState.fields,
      hashState.dateRange,
      sessionDefaultFrom,
      sessionDefaultTo,
    ],
  );

  const tableProps = useCoreApiTableProps({
    schema: schemas.auditLog.getCentralizedAuditLog,
    pageSize: DEFAULT_PAGE_SIZE,
    queryParams: {
      filter: filterForApi,
      offset: (pageNum - 1) * DEFAULT_PAGE_SIZE,
      limit: DEFAULT_PAGE_SIZE,
    },
    initialState: { sortBy: 'updatedAt', sortDir: 'desc' },
  });

  const getDownloadData = useCallback(async (): Promise<ZipCelXSheet> => {
    const records = await fetchAllRecordsInBatches((offset, limit) =>
      coreApiFetch(schemas.auditLog.getCentralizedAuditLog, {
        queryParams: {
          filter: filterForApi,
          offset,
          limit,
          sortBy: tableProps.sortBy,
          sortDir: coreApiTableSortDirToSortDirection(tableProps.sortDir),
        },
      }),
    );

    const exportColumns: Array<DownloadXSheetColumn<CentralizedAuditLogRecordDTO>> = [
      {
        title: 'Date',
        type: 'string',
        value: (r) => formatDate(r.updatedAt, true, true),
      },
      {
        title: 'User',
        type: 'string',
        value: (r) => (r.updatedBy ? formatName(r.updatedBy.firstName, r.updatedBy.lastName) : ''),
      },
      {
        title: 'Field',
        type: 'string',
        value: (r) => (r.field ? snakeCaseToTitleCase(r.field) : ''),
      },
      { title: 'Old Value', type: 'string', value: (r) => r.previousValue ?? '' },
      { title: 'New Value', type: 'string', value: (r) => r.nextValue ?? '' },
      { title: 'Item Code', type: 'string', value: (r) => r.itemCode ?? '' },
      { title: 'Item Name', type: 'string', value: (r) => r.itemName ?? '' },
      { title: 'Location ID', type: 'string', value: (r) => r.locationId ?? '' },
      { title: 'Location', type: 'string', value: (r) => r.locationName ?? '' },
      { title: 'Supplier ID', type: 'string', value: (r) => r.supplierId ?? '' },
      { title: 'Supplier', type: 'string', value: (r) => r.supplierName ?? '' },
    ];

    return recordsToXSheet(records, exportColumns);
  }, [filterForApi, tableProps.sortBy, tableProps.sortDir]);

  const updateFilter = (update: Partial<CentralizedAuditLogsPageHashState>) => {
    updateHashState({ ...update, page: 1 });
  };

  const columns: (ColumnType<CentralizedAuditLogRecordDTO> | null)[] = [
    sortableDateColumn({
      title: 'Date',
      dataIndex: asKeyOf<CentralizedAuditLogRecordDTO>('updatedAt'),
      sorter: true,
      withTimestamp: true,
      inLocalTime: true,
      defaultSortOrder: 'descend',
    }),
    {
      title: 'User',
      dataIndex: asKeyOf<CentralizedAuditLogRecordDTO>('updatedBy'),
      render: (user: CentralizedAuditLogRecordDTO['updatedBy']) =>
        user ? formatName(user.firstName, user.lastName) : '—',
    },
    sortableStringColumn({
      title: 'Field',
      dataIndex: asKeyOf<CentralizedAuditLogRecordDTO>('field'),
      sorter: true,
      render: (field: string) => (field ? snakeCaseToTitleCase(field) : '—'),
    }),
    {
      title: 'Old Value',
      dataIndex: asKeyOf<CentralizedAuditLogRecordDTO>('previousValue'),
      ellipsis: true,
    },
    {
      title: 'New Value',
      dataIndex: asKeyOf<CentralizedAuditLogRecordDTO>('nextValue'),
      ellipsis: true,
    },
    {
      title: 'Item',
      key: 'item',
      render: (_: unknown, record: CentralizedAuditLogRecordDTO) =>
        record.itemCode ? (
          <Link to={routes.purchasing.itemDetails(record.itemCode)}>
            {record.itemCode ?? record.itemName ?? record.itemUid}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_: unknown, record: CentralizedAuditLogRecordDTO) =>
        record.locationId ? (
          <PlanningLink
            to={routes.demandPlanning.planning({
              where: {
                replenishment_location: [
                  joinIdNameObj({ foreignId: record.locationId, name: record.locationName ?? '' }),
                ],
              },
              page: 0,
            })}
          >
            {record.locationName}
          </PlanningLink>
        ) : (
          record.locationName ?? '—'
        ),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_: unknown, record: CentralizedAuditLogRecordDTO) =>
        record.supplierId ? (
          <PlanningLink
            to={routes.demandPlanning.planning({
              where: {
                primary_supplier: [
                  joinIdNameObj({
                    foreignId: record.supplierId,
                    name: record.supplierName ?? '',
                  }),
                ],
              },
              page: 0,
            })}
          >
            {record.supplierName ?? record.supplierId}
          </PlanningLink>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Container>
      <PageHeader
        title="Centralized audit logs"
        subtitle="Review changes to planning-related fields across items, locations, and suppliers."
        headerActions={
          <DownloadButton
            recordType="Audit_logs"
            permission={TenantUserPermission.AccessSettingsMonitoringDownload}
            getDownloadData={getDownloadData}
          />
        }
      />
      <FilterBarBox dividerLine>
        <AsyncMultiSelect
          label="User"
          mode="single"
          showClearButtonOnSingleMode
          icon={<UserOutlined />}
          queryPlaceholder="Search users"
          selectProps={tenantsUsersSelectProps as UseAsyncMultiSelectProps}
          selectedValues={hashState.userId ? [hashState.userId] : []}
          onSelectedValuesChange={(values) => updateFilter({ userId: values[0] })}
        />
        <AsyncMultiSelect
          label="Item"
          mode="single"
          showClearButtonOnSingleMode
          icon={<UnorderedListOutlined />}
          queryPlaceholder="Search items"
          selectProps={itemSelectProps as UseAsyncMultiSelectProps}
          selectedValues={hashState.itemUid ? [hashState.itemUid] : []}
          onSelectedValuesChange={(values) => updateFilter({ itemUid: values[0] })}
        />
        <AsyncMultiSelect
          label="Location"
          mode="single"
          showClearButtonOnSingleMode
          icon={<EnvironmentOutlined />}
          queryPlaceholder="Search locations"
          selectProps={locationsSelectProps as UseAsyncMultiSelectProps}
          selectedValues={hashState.locationValue ? [hashState.locationValue] : []}
          onSelectedValuesChange={(values) => updateFilter({ locationValue: values[0] })}
        />
        <AsyncMultiSelect
          label="Supplier"
          mode="single"
          showClearButtonOnSingleMode
          icon={<ShopOutlined />}
          queryPlaceholder="Search suppliers"
          selectProps={suppliersSelectProps as UseAsyncMultiSelectProps}
          selectedValues={hashState.supplierValue ? [hashState.supplierValue] : []}
          onSelectedValuesChange={(values) => updateFilter({ supplierValue: values[0] })}
        />
        <AsyncMultiSelect
          label="Fields"
          icon={<TagsOutlined />}
          selectProps={{ options: FIELD_OPTIONS }}
          selectedValues={hashState.fields ?? []}
          onSelectedValuesChange={(values) => updateFilter({ fields: values })}
        />
        <DateRangePicker
          value={
            hashState.dateRange
              ? (hashState.dateRange.map((v) => moment(v)) as [Moment, Moment])
              : [moment(sessionDefaultFrom), moment(sessionDefaultTo)]
          }
          onChange={(values) =>
            updateFilter({ dateRange: values ? values.map((m) => m!.format('YYYY-MM-DD')) : undefined })
          }
          format="MM/DD/YYYY"
        />
        {Object.keys(hashState).length > 0 ? (
          <ActionButton
            onClick={() =>
              updateFilter(
                objMapValues(hashState as Obj<unknown>, () => undefined) as Partial<CentralizedAuditLogsPageHashState>,
              )
            }
            label="Clear All"
          />
        ) : null}
        <FlexSpacer />
        <ResultCount count={tableProps.totalCount} />
      </FilterBarBox>
      <AsyncTable
        tableProps={{
          ...tableProps,
          page: hashState.page || 1,
          setPage: (pageNum) => updateHashState({ page: pageNum }),
        }}
        columns={columns.filter(truthy)}
        rowKey={(row) => String(row.centralizedAuditLogId)}
      />
    </Container>
  );
};
